import { useEffect, useState } from "react";

import { Input } from "@workspace/ui/components/input";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
	InputGroupText,
} from "@workspace/ui/components/input-group";

import { useFormatMoneyValue } from "./use-format-money-value";

export function GroupedMoneyInput({
	value,
	onChange,
	currency,
	...props
}: Omit<React.ComponentProps<"input">, "value" | "onChange"> & {
	currency?: string;
	value?: number | null;
	onChange?: (value: number) => void;
}) {
	const formatter = useFormatMoneyValue(currency);
	const [inputValue, setInputValue] = useState("");
	const [isFocused, setIsFocused] = useState(false);

	// Update input value when external value changes and input is not focused
	useEffect(() => {
		if (!isFocused) {
			const display = formatter.to(value);

			// eslint-disable-next-line react-hooks/set-state-in-effect
			setInputValue(display !== null ? display.toString() : "");
		}
	}, [value, formatter, isFocused]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		setInputValue(newValue);

		// Only convert and call onChange if the value is a valid number or empty
		if (newValue === "" || newValue === "0") {
			onChange?.(0);
		} else if (!newValue.endsWith(".") && !isNaN(Number(newValue))) {
			try {
				onChange?.(formatter.from(newValue));
			} catch (_error) {
				// If conversion fails, don't update the value
			}
		}
	};

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		setIsFocused(false);

		// Clean up the input value on blur
		let cleanValue = inputValue.trim();
		if (cleanValue.endsWith(".")) {
			cleanValue = cleanValue.slice(0, -1);
		}

		if (cleanValue === "" || cleanValue === "0") {
			onChange?.(0);
			setInputValue("0");
		} else if (!isNaN(Number(cleanValue))) {
			try {
				const convertedValue = formatter.from(cleanValue);
				onChange?.(convertedValue);
				// Update display with properly formatted value
				const display = formatter.to(convertedValue);
				setInputValue(display !== null ? display.toString() : "");
			} catch (_error) {
				// Reset to previous valid value
				const display = formatter.to(value);
				setInputValue(display !== null ? display.toString() : "");
			}
		} else {
			// Reset to previous valid value if input is invalid
			const display = formatter.to(value);
			setInputValue(display !== null ? display.toString() : "");
		}

		props.onBlur?.(e);
	};

	const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		setIsFocused(true);
		e.target.select();
		props.onFocus?.(e);
	};

	if (currency) {
		return (
			<InputGroup className={props.className}>
				<InputGroupAddon>
					<InputGroupText>{formatter.currency.symbol}</InputGroupText>
				</InputGroupAddon>
				<InputGroupInput
					{...props}
					value={inputValue}
					onChange={handleChange}
					onFocus={handleFocus}
					onBlur={handleBlur}
					type="text"
					inputMode="decimal"
				/>
				<InputGroupAddon align="inline-end">
					<InputGroupText>{formatter.currency.code}</InputGroupText>
				</InputGroupAddon>
			</InputGroup>
		);
	}

	return (
		<Input
			{...props}
			value={inputValue}
			onChange={handleChange}
			onFocus={handleFocus}
			onBlur={handleBlur}
			type="text"
			inputMode="decimal"
		/>
	);
}

export function MoneyInput({
	value,
	onChange,
	currency,
	...props
}: Omit<React.ComponentProps<"input">, "value" | "onChange"> & {
	currency?: string;
	value?: number | null;
	onChange?: (value: number) => void;
}) {
	const formatter = useFormatMoneyValue(currency);
	const [inputValue, setInputValue] = useState("");
	const [isFocused, setIsFocused] = useState(false);

	// Update input value when external value changes and input is not focused
	useEffect(() => {
		if (!isFocused) {
			const display = formatter.to(value);

			// eslint-disable-next-line react-hooks/set-state-in-effect
			setInputValue(display !== null ? display.toString() : "");
		}
	}, [value, formatter, isFocused]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		setInputValue(newValue);

		// Only convert and call onChange if the value is a valid number or empty
		if (newValue === "" || newValue === "0") {
			onChange?.(0);
		} else if (!newValue.endsWith(".") && !isNaN(Number(newValue))) {
			try {
				onChange?.(formatter.from(newValue));
			} catch (_error) {
				// If conversion fails, don't update the value
			}
		}
	};

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		setIsFocused(false);

		// Clean up the input value on blur
		let cleanValue = inputValue.trim();
		if (cleanValue.endsWith(".")) {
			cleanValue = cleanValue.slice(0, -1);
		}

		if (cleanValue === "" || cleanValue === "0") {
			onChange?.(0);
			setInputValue("0");
		} else if (!isNaN(Number(cleanValue))) {
			try {
				const convertedValue = formatter.from(cleanValue);
				onChange?.(convertedValue);
				// Update display with properly formatted value
				const display = formatter.to(convertedValue);
				setInputValue(display !== null ? display.toString() : "");
			} catch (_error) {
				// Reset to previous valid value
				const display = formatter.to(value);
				setInputValue(display !== null ? display.toString() : "");
			}
		} else {
			// Reset to previous valid value if input is invalid
			const display = formatter.to(value);
			setInputValue(display !== null ? display.toString() : "");
		}

		props.onBlur?.(e);
	};

	const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		setIsFocused(true);
		e.target.select();
		props.onFocus?.(e);
	};

	return (
		<Input
			{...props}
			value={inputValue}
			onChange={handleChange}
			onFocus={handleFocus}
			onBlur={handleBlur}
			type="text"
			inputMode="decimal"
		/>
	);
}
